import React from 'react';

interface ChipProps {
  icon?: React.ReactNode;
  title: string;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  variant?: 'default' | 'primary' | 'secondary';
}

export const Chip: React.FC<ChipProps> = ({
  icon,
  title,
  selected = false,
  onClick,
  className = '',
  disabled = false,
  variant = 'default',
}) => {
  const isClickable = !!onClick && !disabled;

  // Base styles for all chips
  const baseStyles = 'inline-flex items-center rounded-full transition-all duration-200';
  
  // Variant styles
  const variantStyles = {
    default: selected
      ? 'bg-primary-100 dark:bg-primary-900/40 border-1 border-primary-500 shadow-[0_2px_8px_rgba(0,0,0,0.1)]'
      : 'bg-white dark:bg-neutral-800 border-1 border-white dark:border-neutral-700 shadow-[0_2px_8px_rgba(0,0,0,0.08)]',
    primary: selected
      ? 'bg-primary-500 border-1 border-primary-600 shadow-[0_2px_8px_rgba(0,0,0,0.15)] text-white'
      : `bg-[#e9edf8] dark:bg-neutral-800 border border-[#814fff44] dark:border-primary-700/40 shadow-[0_2px_8px_rgba(0,0,0,0.08)] text-type-950 dark:text-neutral-100`,
    secondary: selected
      ? 'bg-accent-500 border-1 border-accent-600 shadow-[0_2px_8px_rgba(0,0,0,0.15)] text-white'
      : 'bg-white dark:bg-neutral-800 border-1 border-neutral-300 dark:border-neutral-600 shadow-[0_2px_8px_rgba(0,0,0,0.08)] text-type-800 dark:text-neutral-200',
  };

  // Hover and interaction styles
  const interactionStyles = isClickable
    ? 'cursor-pointer hover:scale-105 hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] active:scale-95'
    : '';

  // Disabled styles
  const disabledStyles = disabled
    ? 'opacity-50 cursor-not-allowed'
    : '';

  // Icon container styles
  const iconContainerStyles = 'flex-shrink-0 w-8 h-8 p-[6px] rounded-full flex items-center justify-center text-[#814fff]';
  
  const iconBackgroundStyles = variant === 'primary' && selected
    ? 'bg-primary-600'
    : variant === 'secondary' && selected
    ? 'bg-accent-600'
    : `bg-[#814fff44]`;

  // Text styles
  const textStyles = 'text-sm font-base whitespace-nowrap flex-1 pl-3 pr-4';

  const textColorStyles = variant === 'primary' && selected
    ? 'text-white'
    : variant === 'secondary' && selected
    ? 'text-white'
    : 'text-type-800 dark:text-neutral-200';

  const combinedClassName = `
    ${baseStyles}
    ${variantStyles[variant]}
    ${interactionStyles}
    ${disabledStyles}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const ChipContent = (
    <>
      {icon && (
        <div className={`${iconContainerStyles} ${iconBackgroundStyles}`}>
          {icon}
        </div>
      )}
      <span className={`${textStyles} ${textColorStyles}`}>
        {title}
      </span>
    </>
  );

  // If clickable, render as button
  if (isClickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={combinedClassName}
        aria-pressed={selected}
      >
        {ChipContent}
      </button>
    );
  }

  // Otherwise, render as div
  return (
    <div className={combinedClassName}>
      {ChipContent}
    </div>
  );
};

